import { useState } from 'react';
import { useApi } from '../lib/api';

interface Methodology {
  id: string;
  name: string;
  description: string;
  category: string;
  complexity: string;
  bestFor: string;
}

interface PhaseAnalysis {
  phase: string;
  covered: boolean;
  score: number;
  durationPct?: number;
  successIndicatorsHit: string[];
  antiPatternsDetected: string[];
  keyMoments: Array<{ transcript: string; assessment: string }>;
  suggestions: string[];
}

interface MethodologyElement {
  code: string;
  name: string;
  score: 'excellent' | 'good' | 'partial' | 'missing';
  evidence: string[];
  suggestions: string[];
}

interface FlowAnalysis {
  followedIntendedOrder: boolean;
  phaseTransitions: Array<{ from: string; to: string; wasSmooth: boolean; note?: string }>;
  timeDistribution: Array<{ phase: string; actualPct: number; suggestedPct: number; assessment: 'good' | 'too_short' | 'too_long' }>;
}

interface AnalysisData {
  narrativeScore: number;
  methodologyScore?: number;
  phases?: PhaseAnalysis[];
  flowAnalysis?: FlowAnalysis;
  methodologyAnalysis?: {
    methodology: string;
    elements: MethodologyElement[];
    overallQualification: 'qualified' | 'needs_work' | 'unqualified';
  };
  strengths: string[];
  improvements: string[];
  coachingTips: string[];
  highlights: Array<{
    type: string;
    description: string;
    transcriptExcerpt: string;
  }>;
}

interface NarrativeAnalysis {
  id: string;
  narrativeScore: string;
  methodologyScore: string | null;
  analysis: AnalysisData;
  phasesCovered: number | null;
  phasesTotal: number | null;
  qualificationStatus: string | null;
  llmModel: string | null;
  processingTimeMs: number | null;
  createdAt: string;
}

interface Props {
  sessionId: string;
  hasTranscript: boolean;
}

const SCORE_LABELS: Record<string, { color: string; label: string }> = {
  excellent: { color: 'text-green-400', label: 'Excellent' },
  good: { color: 'text-blue-400', label: 'Good' },
  partial: { color: 'text-yellow-400', label: 'Partial' },
  missing: { color: 'text-red-400', label: 'Missing' },
};

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-green-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400';
  return (
    <span className={`text-3xl font-bold ${color}`}>
      {Math.round(score)}
      <span className="text-lg text-slate-400">/100</span>
    </span>
  );
}

function QualificationBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    qualified: 'bg-green-500/20 text-green-400 border-green-500/30',
    needs_work: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    unqualified: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  const labels: Record<string, string> = {
    qualified: 'Qualified',
    needs_work: 'Needs Work',
    unqualified: 'Unqualified',
  };
  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full border ${styles[status] || styles.needs_work}`}>
      {labels[status] || status}
    </span>
  );
}

export function NarrativeReport({ sessionId, hasTranscript }: Props) {
  const { get, post } = useApi();
  const [analysis, setAnalysis] = useState<NarrativeAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [methodologies, setMethodologies] = useState<Methodology[]>([]);
  const [selectedMethodology, setSelectedMethodology] = useState<string>('');
  const [showMethodologyPicker, setShowMethodologyPicker] = useState(false);

  // Load existing analysis
  async function loadAnalysis() {
    try {
      setLoading(true);
      setError(null);
      const data = await get<NarrativeAnalysis>(`/sessions/${sessionId}/narrative`);
      setAnalysis(data);
    } catch {
      // 404 means no analysis exists yet — that's fine
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  // Load methodologies for picker
  async function loadMethodologies() {
    try {
      const data = await get<{ methodologies: Methodology[] }>('/methodologies');
      setMethodologies(data.methodologies);
    } catch {
      // If methodologies aren't seeded yet, that's OK
    }
  }

  // Trigger new analysis
  async function runAnalysis() {
    try {
      setAnalyzing(true);
      setError(null);
      const body: Record<string, string> = {};
      if (selectedMethodology) {
        body.methodologyId = selectedMethodology;
      }
      const data = await post<NarrativeAnalysis>(`/sessions/${sessionId}/narrative/analyze`, body);
      setAnalysis(data);
      setShowMethodologyPicker(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }

  // Auto-load on first render
  if (!loaded && !loading) {
    loadAnalysis();
  }

  if (loading) {
    return (
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500" />
          <span className="text-slate-400">Loading analysis...</span>
        </div>
      </div>
    );
  }

  // No analysis yet — show trigger button
  if (!analysis) {
    return (
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Narrative Analysis</h2>
          <p className="text-sm text-slate-400 mt-1">
            AI-powered analysis of your call structure and sales methodology execution
          </p>
        </div>
        <div className="p-6">
          {!hasTranscript ? (
            <div className="text-center">
              <p className="text-slate-400">
                Transcript required for narrative analysis.
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Transcript storage is available on Team plans.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {showMethodologyPicker ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-300">
                    Optionally select a sales methodology to score against:
                  </p>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedMethodology('')}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                        !selectedMethodology
                          ? 'bg-green-500/10 border-green-500/50 text-green-400'
                          : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <div className="font-medium">General Narrative Analysis</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Evaluate overall call structure, flow, and coaching opportunities
                      </div>
                    </button>
                    {methodologies.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setSelectedMethodology(m.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                          selectedMethodology === m.id
                            ? 'bg-green-500/10 border-green-500/50 text-green-400'
                            : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600'
                        }`}
                      >
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {m.description?.slice(0, 100)}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowMethodologyPicker(false)}
                      className="flex-1 py-2.5 px-4 text-sm font-medium text-slate-400 bg-slate-700 rounded-lg hover:bg-slate-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={runAnalysis}
                      disabled={analyzing}
                      className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {analyzing ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Analyzing...
                        </span>
                      ) : (
                        'Run Analysis'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    onClick={async () => {
                      await loadMethodologies();
                      setShowMethodologyPicker(true);
                    }}
                    className="px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-500 transition-colors"
                  >
                    Analyze This Session
                  </button>
                  <p className="text-xs text-slate-500 mt-2">
                    Uses AI to evaluate your call structure, identify strengths, and suggest improvements
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render full analysis report
  const data = analysis.analysis;

  return (
    <div className="space-y-4">
      {/* Score Cards */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Narrative Analysis</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {analysis.llmModel && `Model: ${analysis.llmModel}`}
              {analysis.processingTimeMs && ` | ${(analysis.processingTimeMs / 1000).toFixed(1)}s`}
            </p>
          </div>
          <button
            onClick={async () => {
              await loadMethodologies();
              setShowMethodologyPicker(true);
              setAnalysis(null);
            }}
            className="px-3 py-1.5 text-xs font-medium text-slate-400 bg-slate-700 rounded-md hover:bg-slate-600 transition-colors"
          >
            Re-analyze
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-400 mb-2">Narrative Score</p>
              <ScoreBadge score={data.narrativeScore} />
            </div>
            {data.methodologyScore != null && (
              <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-400 mb-2">
                  {data.methodologyAnalysis?.methodology || 'Methodology'} Score
                </p>
                <ScoreBadge score={data.methodologyScore} />
              </div>
            )}
            {data.methodologyAnalysis?.overallQualification && (
              <div className="bg-slate-900/50 rounded-lg p-4 flex flex-col items-center justify-center">
                <p className="text-sm text-slate-400 mb-2">Qualification</p>
                <QualificationBadge status={data.methodologyAnalysis.overallQualification} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Methodology Elements */}
      {data.methodologyAnalysis && data.methodologyAnalysis.elements.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">
              {data.methodologyAnalysis.methodology} Breakdown
            </h2>
          </div>
          <div className="divide-y divide-slate-700">
            {data.methodologyAnalysis.elements.map((el) => {
              const scoreInfo = SCORE_LABELS[el.score] || SCORE_LABELS.missing;
              return (
                <div key={el.code} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                        {el.code}
                      </span>
                      <span className="text-white font-medium">{el.name}</span>
                    </div>
                    <span className={`text-sm font-medium ${scoreInfo.color}`}>
                      {scoreInfo.label}
                    </span>
                  </div>
                  {el.evidence.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {el.evidence.map((e, i) => (
                        <p key={i} className="text-sm text-slate-400 pl-4 border-l-2 border-slate-700">
                          {e}
                        </p>
                      ))}
                    </div>
                  )}
                  {el.suggestions.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {el.suggestions.map((s, i) => (
                        <p key={i} className="text-sm text-blue-400 pl-4 border-l-2 border-blue-500/30">
                          {s}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase Analysis */}
      {data.phases && data.phases.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Phase Breakdown</h2>
            <p className="text-sm text-slate-400">
              {data.phases.filter((p) => p.covered).length} of {data.phases.length} phases covered
            </p>
          </div>
          <div className="divide-y divide-slate-700">
            {data.phases.map((phase) => (
              <PhaseRow key={phase.phase} phase={phase} />
            ))}
          </div>
        </div>
      )}

      {/* Time Distribution */}
      {data.flowAnalysis?.timeDistribution && data.flowAnalysis.timeDistribution.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Time Distribution</h2>
            {data.flowAnalysis.followedIntendedOrder != null && (
              <p className="text-sm text-slate-400">
                {data.flowAnalysis.followedIntendedOrder
                  ? 'Followed intended phase order'
                  : 'Deviated from intended phase order'}
              </p>
            )}
          </div>
          <div className="p-6 space-y-3">
            {data.flowAnalysis.timeDistribution.map((td) => {
              const assessColor =
                td.assessment === 'good' ? 'text-green-400' :
                td.assessment === 'too_short' ? 'text-yellow-400' : 'text-red-400';
              return (
                <div key={td.phase} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{td.phase}</span>
                    <span className={assessColor}>
                      {td.actualPct}%
                      <span className="text-slate-500 ml-1">(target: {td.suggestedPct}%)</span>
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        td.assessment === 'good' ? 'bg-green-500' :
                        td.assessment === 'too_short' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(td.actualPct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strengths & Improvements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.strengths.length > 0 && (
          <div className="bg-slate-800 rounded-lg border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-green-400">Strengths</h2>
            </div>
            <div className="p-6 space-y-2">
              {data.strengths.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 flex-shrink-0">+</span>
                  <p className="text-sm text-slate-300">{s}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.improvements.length > 0 && (
          <div className="bg-slate-800 rounded-lg border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-yellow-400">Areas to Improve</h2>
            </div>
            <div className="p-6 space-y-2">
              {data.improvements.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-yellow-400 mt-0.5 flex-shrink-0">-</span>
                  <p className="text-sm text-slate-300">{s}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Coaching Tips */}
      {data.coachingTips && data.coachingTips.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-blue-400">Coaching Tips</h2>
          </div>
          <div className="p-6 space-y-2">
            {data.coachingTips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5 flex-shrink-0">*</span>
                <p className="text-sm text-slate-300">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Moments / Highlights */}
      {data.highlights && data.highlights.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Key Moments</h2>
          </div>
          <div className="divide-y divide-slate-700">
            {data.highlights.map((h, i) => {
              const typeStyles: Record<string, { icon: string; color: string }> = {
                great_question: { icon: '*', color: 'text-green-400' },
                good_proof: { icon: '+', color: 'text-blue-400' },
                missed_opportunity: { icon: '!', color: 'text-yellow-400' },
                prospect_signal: { icon: '>', color: 'text-purple-400' },
              };
              const style = typeStyles[h.type] || typeStyles.prospect_signal;
              return (
                <div key={i} className="px-6 py-4">
                  <div className="flex items-start gap-2 mb-1">
                    <span className={`${style.color} font-bold`}>{style.icon}</span>
                    <span className={`text-sm font-medium ${style.color}`}>
                      {h.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-white mb-1">{h.description}</p>
                  <p className="text-sm text-slate-400 italic pl-4 border-l-2 border-slate-700">
                    &quot;{h.transcriptExcerpt}&quot;
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PhaseRow({ phase }: { phase: PhaseAnalysis }) {
  const [expanded, setExpanded] = useState(false);
  const scoreColor = phase.score >= 80 ? 'text-green-400' : phase.score >= 60 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
            phase.covered ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {phase.covered ? '+' : '-'}
          </span>
          <span className="text-white">{phase.phase}</span>
          {phase.durationPct != null && (
            <span className="text-xs text-slate-500">{phase.durationPct}% of call</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${scoreColor}`}>{Math.round(phase.score)}/100</span>
          <span className="text-slate-500">{expanded ? '-' : '+'}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-6 pb-4 space-y-3">
          {phase.successIndicatorsHit.length > 0 && (
            <div>
              <p className="text-xs font-medium text-green-400 mb-1">What went well:</p>
              {phase.successIndicatorsHit.map((s, i) => (
                <p key={i} className="text-sm text-slate-400 pl-3">+ {s}</p>
              ))}
            </div>
          )}
          {phase.antiPatternsDetected.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-400 mb-1">Anti-patterns detected:</p>
              {phase.antiPatternsDetected.map((s, i) => (
                <p key={i} className="text-sm text-slate-400 pl-3">- {s}</p>
              ))}
            </div>
          )}
          {phase.keyMoments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-300 mb-1">Key moments:</p>
              {phase.keyMoments.map((m, i) => (
                <div key={i} className="pl-3 mb-2">
                  <p className="text-sm text-slate-400 italic">&quot;{m.transcript}&quot;</p>
                  <p className="text-xs text-slate-500">{m.assessment}</p>
                </div>
              ))}
            </div>
          )}
          {phase.suggestions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-blue-400 mb-1">Suggestions:</p>
              {phase.suggestions.map((s, i) => (
                <p key={i} className="text-sm text-slate-400 pl-3">* {s}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
